import { StatusTypes } from '../../core/models/events-models';
import {
  MessageFileLink,
  MessageText,
  MessageUrl,
} from '../../core/models/messages-models';
import { Provider } from '../provider-abstract';
import { requestChatMessage } from './models/requests-model';
export class AvayaProvider extends Provider {
  constructor() {
    super(...arguments);
    this.agentNameIdMap = [];
    this.eventMapping = {
      newMessage: this._newMessage,
      isTyping: this._isTyping,
      error: this._error,
      newParticipant: this._newParticipant,
      participantLeave: this._participantLeave,
      requestChat: this._requestChat,
      newPushPageMessage: this._newPushPageMessage,
      newAgentFileTransfer: this._newAgentFileTransfer,
      closeConversation: this._closeConversation,
      routeCancel: this._routeCancel,
      pong: this.signalPongReceived,
    };
  }
  onInitiateChat() {
    const message = requestChatMessage(
      {
        firstName: this.providerGatewayData.memberFirstName,
        lastName: this.providerGatewayData.memberLastName,
        phoneNumber: this.providerGatewayData.phoneNumber,
      },
      this.providerGatewayData.contextId,
      {
        emailAddress: this.providerGatewayData.emailAddress,
      },
      this.providerGatewayData.teamCode,
      this.providerGatewayData.reasonCode,
    );
    this.send(message);
  }
  onReconnectChat() {
    this.send({
      method: 'renewChat',
      guid: this.session.userInfo.guid,
      authenticationKey: this.session.userInfo.authenticationKey,
      requestFullTranscript: false,
    });
  }
  sendMessageHandler(message) {
    this.send({
      method: 'newMessage',
      message: message,
      type: 'text',
      data: { message },
    });
  }
  sendIsTypingHandler() {
    this.send({
      method: 'isTyping',
      isTyping: true,
    });
  }
  sendPing() {
    this.send({ method: 'ping' });
  }
  onChatClose() {
    this.send({ method: 'closeConversation' });
  }
  sendEventHandler() {}
  onTransferResult() {}
  webSocketMessageHandler(json) {
    var _a;
    const msg = JSON.parse(json.data.toString());
    const actionName =
      (_a = msg.body) === null || _a === void 0 ? void 0 : _a.method;
    const eventHandler = this.eventMapping[actionName];
    if (!eventHandler) {
      this.session.chatInstance.emitError(
        'MESSAGE_RECEIVE_FAILURE',
        'Bad avaya message! Unsupported action!!',
      );
      return;
    }
    eventHandler.bind(this)(msg);
  }
  buildAvayaAvatar(name) {
    const avatarUrl = 'https://cdn.highmark.com/content/chat/live-agent.png';
    const avatarLabel = `Customer Service Advocate Avatar: ${name}`;
    const urlContent = {
      source: 'HREF',
      linkType: 'EXTERNAL',
      value: avatarUrl,
    };
    const image = { imageType: 'URL', url: urlContent };
    return { icon: image, iconAltText: avatarLabel };
  }
  send(body) {
    this.sendToWebSocket({
      authToken: '',
      apiVersion: '1.0',
      type: 'request',
      body,
    });
  }
  _newParticipant({ body }) {
    const newParticipant = body;
    if (
      newParticipant.role !== 'active_participant' &&
      newParticipant.role !== 'supervisor_barge'
    ) {
      return;
    }
    if (this.session.chatInstance.getCurrentChatState() === 'QUEUED') {
      this.session.chatInstance.emitStatusChange(
        this.providerIdentifier,
        StatusTypes.CONNECTED,
      );
      const message = [
        `Email: ${this.providerGatewayData.emailAddress}`,
        `UMI of member signed in: ${this.providerGatewayData.umi}`,
        `DOB of member signed in: ${this.providerGatewayData.birthDate}`,
        `Person chatting about: ${this.providerGatewayData.memberFirstName} ${this.providerGatewayData.memberLastName} `,
        `Selected member name: ${this.providerGatewayData.selectedMemberName}`,
        `Selected member ID: ${this.providerGatewayData.selectedMemberId}`,
        `Reason for chat: ${this.providerGatewayData.reasonCode}`,
      ].join('\n');
      const transcripts = this.providerGatewayData.chatTranscript;
      transcripts === null || transcripts === void 0
        ? void 0
        : transcripts.forEach((transcript) => {
            this.sendMessageHandler(transcript);
          });
      this.sendMessageHandler(message);
    }
    this.session.chatInstance.emitParticipantJoin(
      this.providerIdentifier,
      Object.assign(
        {
          firstName: newParticipant.displayName,
          displayName: newParticipant.displayName,
          id: newParticipant.agentId,
          title: '',
          lastName: '',
        },
        this.buildAvayaAvatar(newParticipant.displayName),
      ),
    );
    this.agentNameIdMap.push({
      name: newParticipant.displayName,
      id: newParticipant.agentId,
    });
  }
  _participantLeave({ body }) {
    const leaveParticipant = body;
    let agentName = undefined;
    this.agentNameIdMap.forEach((agent, index) => {
      if (agent.id === leaveParticipant.agentId) {
        agentName = agent.name;
        this.agentNameIdMap.splice(index, 1);
        return;
      }
    });
    if (!agentName) {
      return;
    }
    this.session.chatInstance.emitParticipantLeave(
      this.providerIdentifier,
      Object.assign(
        {
          firstName: agentName,
          displayName: agentName,
          id: leaveParticipant.agentId,
          title: '',
          lastName: '',
        },
        this.buildAvayaAvatar(agentName),
      ),
    );
    if (leaveParticipant.endChatFlag) {
      this.closeChat();
      this.session.chatInstance.emitChatEnd('PROVIDER');
    } else if (
      /^(transfer|transfer_to_(agent|user))$/i.test(
        leaveParticipant.leaveReason,
      ) &&
      leaveParticipant.numberOfParticipants === 0
    ) {
      this.session.chatInstance.emitTransferRequestInternal('new agent');
      this.session.chatInstance.emitStatusChange(
        this.providerIdentifier,
        StatusTypes.QUEUED,
      );
    }
  }
  _isTyping({ body }) {
    const isTyping = body;
    this.session.chatInstance.emitIsTyping(
      Object.assign(
        {
          firstName: isTyping.displayName,
          displayName: isTyping.displayName,
          id: isTyping.agentId,
          title: '',
          lastName: '',
        },
        this.buildAvayaAvatar(isTyping.displayName),
      ),
    );
  }
  _newMessage({ body }) {
    if (body.accepted) {
      this.messageAcknowledgementReceived();
      return;
    }
    const newMessage = body;
    const agentId = this.agentNameIdMap.find(
      (agent) => agent.name === newMessage.displayName,
    ).id;
    this.session.chatInstance.emitNewMessage(
      this.providerIdentifier,
      Object.assign(
        {
          firstName: newMessage.displayName,
          displayName: newMessage.displayName,
          id: agentId,
          title: '',
          lastName: '',
        },
        this.buildAvayaAvatar(newMessage.displayName),
      ),
      [new MessageText(newMessage.message)],
    );
  }
  _newPushPageMessage({ body }) {
    const pushPage = body;
    const agentId = this.agentNameIdMap.find(
      (agent) => agent.name === pushPage.displayName,
    ).id;
    this.session.chatInstance.emitNewMessage(
      this.providerIdentifier,
      Object.assign(
        {
          firstName: pushPage.displayName,
          displayName: pushPage.displayName,
          id: agentId,
          title: '',
          lastName: '',
        },
        this.buildAvayaAvatar(pushPage.displayName),
      ),
      [
        new MessageUrl({
          source: 'HREF',
          linkType: 'EXTERNAL',
          value: pushPage.pagePushURL,
        }),
      ],
    );
  }
  _newAgentFileTransfer({ body }) {
    const fileInfo = body;
    const fileDownloadLink =
      'https://' +
      this.providerConfig.baseURL +
      '/services/customer/rest/attachment/' +
      fileInfo.uuid +
      '?workRequestId=' +
      fileInfo.workRequestId;
    this.session.chatInstance.emitNewMessage(
      this.providerIdentifier,
      Object.assign(
        {
          firstName: this.agentNameIdMap[0].name,
          displayName: this.agentNameIdMap[0].name,
          id: this.agentNameIdMap[0].id,
          title: '',
          lastName: '',
        },
        this.buildAvayaAvatar(this.agentNameIdMap[0].name),
      ),
      [
        new MessageFileLink(
          {
            name: fileInfo.name,
            mimeType: fileInfo.mimeType,
            size: fileInfo.size,
          },
          {
            source: 'HREF',
            linkType: 'EXTERNAL',
            value: fileDownloadLink,
          },
        ),
      ],
    );
  }
  _routeCancel() {
    this._closeConversation();
  }
  _requestChat({ body }) {
    const msg = body;
    this.session.userInfo.guid = msg.guid;
    this.session.userInfo.authenticationKey = msg.authenticationKey;
    this.session.userInfo.workRequestId = msg.workRequestId;
    if (
      this.session.chatInstance.getCurrentChatState() ===
      StatusTypes.RECONNECTING
    ) {
      this.session.chatInstance.emitStatusChange(
        this.providerIdentifier,
        StatusTypes.CONNECTED,
      );
    } else {
      this.session.chatInstance.emitStatusChange(
        this.providerIdentifier,
        StatusTypes.QUEUED,
      );
    }
  }
  _closeConversation() {
    this.closeChat();
    this.session.chatInstance.emitChatEnd('PROVIDER');
  }
  _error({ body }) {
    var _a;
    const errorMsg = body;
    const errorMap = {
      'invalid message': 'MESSAGE_SEND_INVALID',
      'session has expired': 'PROVIDER_ERROR',
      'invalid request': 'REQUEST_CHAT_INVALID',
      'unable to communicate': 'PROVIDER_UNAVAILABLE',
    };
    const errorCode =
      (_a = errorMap[errorMsg.errorMessage.toLowerCase()]) !== null &&
      _a !== void 0
        ? _a
        : 'GENERAL_ERROR';
    this.session.chatInstance.emitError(
      errorCode,
      errorMsg.errorMessage,
      errorMsg.needToClose,
    );
    if (errorMsg.needToClose) {
      this.closeChat();
      this.session.chatInstance.emitChatEnd('ERROR');
    }
  }
}
//# sourceMappingURL=avaya-provider.js.map
